"use client";

import type React from "react";

import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  Bell,
  BookOpen,
  CalendarIcon,
  Coins,
  CreditCard,
  DollarSign,
  FileText,
  HomeIcon,
  LucideUser,
  MessageCircle,
  MessageCircleQuestion,
  Settings,
  StickyNote,
  Users,
} from "lucide-react";
import { Icon } from "@iconify-icon/react";

import PATHS, { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths.ts";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import useUserState from "@/hooks/useUserState.tsx";
import { useCurrentWorkspaceStore } from "@/features/workspaces/store/useCurrentWorkspaceStore.tsx";
import useUserStore from "@/features/auth/store/useUserStore";

export interface AppLink {
  id: number;
  title: string;
  icon: React.ReactNode;
  path: string;
  pattern: RegExp; // Regex pattern to match active state
  children?: AppLinkChild[]; // Optional children for collapsible items
  disabled?: boolean; // Added optional disabled property
  soon?: boolean; // Added soon property
  // Coach marker properties
  coachMark?: {
    id: string;
    title: string;
    description: string;
    placement?: "left" | "top" | "bottom" | "right";
    shouldShow: boolean;
  };
}

export interface AppLinkChild {
  id: number;
  title: string;
  path: string;
  pattern: RegExp;
  icon?: React.ReactNode;
  soon?: boolean;
  disabled?: boolean; // Added optional disabled property
  // Coach marker properties
  coachMark?: {
    id: string;
    title: string;
    description: string;
    placement?: "left" | "top" | "bottom" | "right";
    shouldShow: boolean;
  };
}

export interface AppLinkGroup {
  id: number;
  label: string; // Added icon for category
  links: AppLink[];
}

export function useSidebarLinks() {
  const { t } = useTranslation(["sidebar", "help"]);
  const { data: workspaceData } = useWorkspaceInformationStore();
  const { currentWorkspace } = useCurrentWorkspaceStore();
  const workspaceId = currentWorkspace?.id;
  const location = useLocation();
  const userState = useUserState();
  const { user } = useUserStore();

  const linkGroups = useMemo(() => {
    // Helper function to determine if coach markers should show for tutors
    const shouldShowCoachMarkers = userState === "tutor" || userState === "tutorInWorkspace";
    const shouldShowStudentCoachMarkers =
      userState === "student" || userState === "studentInWorkspace";

    // Base tutor links (General, Finances, Support)
    const baseTutorLinks: AppLinkGroup[] = [
      {
        id: 0,
        label: t("general"),
        links: [
          {
            id: 1,
            title: t("tutor.overview", "Overview"),
            icon: <HomeIcon height="1.125rem" width="1.125rem" />,
            path: PATHS.TUTOR.OVERVIEW,
            pattern: new RegExp(`^${PATHS.TUTOR.OVERVIEW}(/.*)?$`),
            coachMark: {
              id: "sidebar-tutor-overview",
              title: t("help:coachMarkers.sidebar.tutor.overview.title"),
              description: t("help:coachMarkers.sidebar.tutor.overview.description"),
              placement: "right",
              shouldShow: shouldShowCoachMarkers,
            },
          },
          {
            id: 2,
            title: t("tutor.public_profile"),
            icon: <LucideUser height="1.125rem" width="1.125rem" />,
            path: PATHS.TUTOR.PUBLIC_PROFILE,
            pattern: new RegExp(`^${PATHS.TUTOR.PUBLIC_PROFILE}(/.*)?$`),
            coachMark: {
              id: "sidebar-tutor-public-profile",
              title: t("help:coachMarkers.sidebar.tutor.public_profile.title"),
              description: t("help:coachMarkers.sidebar.tutor.public_profile.description"),
              placement: "right",
              shouldShow: shouldShowCoachMarkers,
            },
          },
          {
            id: 3,
            title: t("tutor.lessons_schedule"),
            icon: <CalendarIcon height="1.125rem" width="1.125rem" />,
            path: "#",
            children: [
              {
                id: 1,
                title: t("tutor.calendar"),
                icon: <CalendarIcon height="1rem" width="1rem" />,
                path: PATHS.TUTOR.CALENDAR,
                pattern: new RegExp(`^${PATHS.TUTOR.CALENDAR}(/.*)?`),
              },
              {
                id: 2,
                title: t("tutor.templates"),
                icon: <FileText height="1rem" width="1rem" />,
                path: PATHS.TUTOR.SCHEDULE.TEMPLATES,
                pattern: new RegExp(`^${PATHS.TUTOR.SCHEDULE.TEMPLATES}(/.*)?`),
              },
              {
                id: 3,
                title: t("tutor.settings"),
                icon: <Settings height="1rem" width="1rem" />,
                path: PATHS.TUTOR.SCHEDULE.SETTINGS,
                pattern: new RegExp(`^${`${PATHS.TUTOR.SCHEDULE.SETTINGS}`}(/.*)?`),
              },
              {
                id: 4,
                title: t("tutor.lessonPricing"),
                icon: <Banknote height="1rem" width="1rem" />,
                path: PATHS.TUTOR.SCHEDULE.PRICING,
                pattern: new RegExp(`^${`${PATHS.TUTOR.SCHEDULE.PRICING}`}(/.*)?`),
              },
            ],
            pattern: new RegExp(`^${PATHS.TUTOR.CALENDAR}(/.*)`),
            coachMark: {
              id: "sidebar-tutor-lessons-schedule",
              title: t("help:coachMarkers.sidebar.tutor.lessons_schedule.title"),
              description: t("help:coachMarkers.sidebar.tutor.lessons_schedule.description"),
              placement: "right",
              shouldShow: shouldShowCoachMarkers,
            },
          },
          {
            id: 4,
            title: t("workspaces", "Workspaces"),
            icon: <Icon height="1.125rem" icon="mdi:view-dashboard" width="1.125rem" />,
            path: "#",
            children: [
              {
                id: 1,
                title: t("list", "List"),
                icon: <Icon height="1rem" icon="mdi:view-list" width="1rem" />,
                path: PATHS.TUTOR.WORKSPACES.LIST,
                pattern: new RegExp(`^${PATHS.TUTOR.WORKSPACES.LIST}(/.*)?$`),
              },
              {
                id: 2,
                title: t("invitations", "Invitations"),
                icon: <Icon height="1rem" icon="mdi:email-open" width="1rem" />,
                path: PATHS.TUTOR.WORKSPACES.INVITATIONS,
                pattern: new RegExp(`^${PATHS.TUTOR.WORKSPACES.INVITATIONS}(/.*)?$`),
              },
            ],
            pattern: new RegExp(`^${PATHS.TUTOR.WORKSPACES.WORKSPACES}/(list|invitations)$`),
            coachMark: {
              id: "sidebar-tutor-workspaces",
              title: t("help:coachMarkers.sidebar.tutor.workspaces.title"),
              description: t("help:coachMarkers.sidebar.tutor.workspaces.description"),
              placement: "right",
              shouldShow: shouldShowCoachMarkers,
            },
          },
        ],
      },
      {
        id: 2,
        label: t("tutor.finances", "Finances"),
        links:
          user?.specialRoleChecks?.payment_plan === "credits"
            ? [
                {
                  id: 1,
                  title: t("tutor.overview", "Overview"),
                  icon: <DollarSign height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.OVERVIEW,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.OVERVIEW}(/.*)?$`),
                  coachMark: {
                    id: "sidebar-tutor-finances",
                    title: t("help:coachMarkers.sidebar.tutor.finances.title"),
                    description: t("help:coachMarkers.sidebar.tutor.finances.description"),
                    placement: "right",
                    shouldShow: shouldShowCoachMarkers,
                  },
                },
                {
                  id: 2,
                  title: t("tutor.buyCredits", "Buy Credits"),
                  icon: <Icon height="1.125rem" icon="tabler:coins" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.BUY_CREDITS,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.BUY_CREDITS}(/.*)?$`),
                },
                {
                  id: 3,
                  title: t("tutor.creditsHistory", "Credits History"),
                  icon: <Icon height="1.125rem" icon="mdi:history" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.CREDITS_HISTORY,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.CREDITS_HISTORY}(/.*)?$`),
                  soon: true,
                },
                {
                  id: 4,
                  title: t("tutor.payments", "Payments"),
                  icon: <CreditCard height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY}(/.*)?$`),
                },
              ]
            : [
                {
                  id: 1,
                  title: t("tutor.overview", "Overview"),
                  icon: <DollarSign height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.OVERVIEW,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.OVERVIEW}(/.*)?$`),
                  coachMark: {
                    id: "sidebar-tutor-finances",
                    title: t("help:coachMarkers.sidebar.tutor.finances.title"),
                    description: t("help:coachMarkers.sidebar.tutor.finances.description"),
                    placement: "right",
                    shouldShow: shouldShowCoachMarkers,
                  },
                },
                {
                  id: 2,
                  title: t("tutor.payments", "Payments"),
                  icon: <CreditCard height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY}(/.*)?$`),
                },
                {
                  id: 3,
                  title: t("tutor.payouts", "Payouts"),
                  icon: <Coins height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.PAYOUTS,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.PAYOUTS}(/.*)?$`),
                },
                {
                  id: 4,
                  title: t("tutor.settings"),
                  icon: <Settings height="1.125rem" width="1.125rem" />,
                  path: PATHS.TUTOR.FINANCES.SETTINGS,
                  pattern: new RegExp(`^${PATHS.TUTOR.FINANCES.SETTINGS}(/.*)?$`),
                  soon: true,
                },
              ],
      },
      {
        id: 3,
        label: t("support", "Support"),
        links: [
          {
            id: 1,
            title: t("technical_support", "Technical Support"),
            icon: <MessageCircleQuestion height="1.125rem" width="1.125rem" />,
            path: PATHS.TUTOR.SUPPORT.TECHNICAL,
            pattern: new RegExp(`^${PATHS.TUTOR.SUPPORT.TECHNICAL}(/.*)?$`),
            coachMark: {
              id: "sidebar-tutor-technical-support",
              title: t("help:coachMarkers.sidebar.tutor.technical_support.title"),
              description: t("help:coachMarkers.sidebar.tutor.technical_support.description"),
              placement: "right",
              shouldShow: shouldShowCoachMarkers,
            },
          },
        ],
      },
    ];

    // Base student links (General, Finances, Support)
    const baseStudentLinks: AppLinkGroup[] = [
      {
        id: 0,
        label: t("general"),
        links: [
          {
            id: 1,
            title: t("student.overview", "Overview"),
            icon: <HomeIcon height="1.125rem" width="1.125rem" />,
            path: PATHS.STUDENT.OVERVIEW,
            pattern: new RegExp(`^${PATHS.STUDENT.OVERVIEW}(/.*)?$`),
            coachMark: {
              id: "sidebar-student-overview",
              title: t("help:coachMarkers.sidebar.student.overview.title"),
              description: t("help:coachMarkers.sidebar.student.overview.description"),
              placement: "right",
              shouldShow: shouldShowStudentCoachMarkers,
            },
          },
          {
            id: 2,
            title: t("workspaces", "Workspaces"),
            icon: <Icon height="1.125rem" icon="mdi:view-dashboard" width="1.125rem" />,
            path: PATHS.STUDENT.WORKSPACES.LIST,
            pattern: new RegExp(`^${PATHS.STUDENT.WORKSPACES.LIST}(/.*)?$`),
            coachMark: {
              id: "sidebar-student-workspaces",
              title: t("help:coachMarkers.sidebar.student.workspaces.title"),
              description: t("help:coachMarkers.sidebar.student.workspaces.description"),
              placement: "right",
              shouldShow: shouldShowStudentCoachMarkers,
            },
          },
        ],
      },
      {
        id: 2,
        label: t("student.finances", "Finances"),
        links: [
          {
            id: 1,
            title: t("student.payments", "Payments"),
            icon: <CreditCard height="1.125rem" width="1.125rem" />,
            path: PATHS.STUDENT.FINANCES.PAYMENTS,
            pattern: new RegExp(`^${PATHS.STUDENT.FINANCES.PAYMENTS}(/.*)?$`),
          },
        ],
      },
      {
        id: 3,
        label: t("support", "Support"),
        links: [
          {
            id: 1,
            title: t("technical_support", "Technical Support"),
            icon: <MessageCircleQuestion height="1.125rem" width="1.125rem" />,
            path: PATHS.STUDENT.SUPPORT.TECHNICAL,
            pattern: new RegExp(`^${PATHS.STUDENT.SUPPORT.TECHNICAL}(/.*)?$`),
            coachMark: {
              id: "sidebar-student-support",
              title: t("help:coachMarkers.sidebar.student.technical_support.title"),
              description: t("help:coachMarkers.sidebar.student.technical_support.description"),
              placement: "right",
              shouldShow: shouldShowStudentCoachMarkers,
            },
          },
        ],
      },
    ];

    // Main workspace links for tutor
    const tutorMainWorkspaceLinks: AppLinkGroup = {
      id: 1,
      label: t("mainWorkspace", "Main Workspace"),
      links: [
        {
          id: 1,
          title: t("students", "Students"),
          icon: <Users height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS("main").STUDENTS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS("main").STUDENTS}(/.*)?$`),
          coachMark: {
            id: "sidebar-tutor-main-students",
            title: t("help:coachMarkers.sidebar.tutor.main_students.title"),
            description: t("help:coachMarkers.sidebar.tutor.main_students.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 2,
          title: t("lessons", "Lessons"),
          icon: <BookOpen height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS("main").LESSONS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS("main").LESSONS}(/.*)?$`),
          coachMark: {
            id: "sidebar-tutor-main-lessons",
            title: t("help:coachMarkers.sidebar.tutor.main_lessons.title"),
            description: t("help:coachMarkers.sidebar.tutor.main_lessons.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 3,
          title: t("conversations", "Conversations"),
          icon: <MessageCircle height="1.125rem" width="1.125rem" />,
          path: `${TUTOR_WORKSPACE_PATHS("main").CHATS}`,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS("main").CHATS}/.*$`),
          coachMark: {
            id: "sidebar-tutor-main-conversations",
            title: t("help:coachMarkers.sidebar.tutor.main_conversations.title"),
            description: t("help:coachMarkers.sidebar.tutor.main_conversations.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
      ],
    };

    // Main workspace links for student
    const studentMainWorkspaceLinks: AppLinkGroup = {
      id: 1,
      label: t("mainWorkspace", "Main Workspace"),
      links: [
        {
          id: 1,
          title: t("tutors", "Tutors"),
          icon: <Users height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS("main").TUTORS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").TUTORS}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-main-tutors",
            title: t("help:coachMarkers.sidebar.student.main_tutors.title"),
            description: t("help:coachMarkers.sidebar.student.main_tutors.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 2,
          title: t("lessons", "Lessons"),
          icon: <BookOpen height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS("main").LESSONS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").LESSONS}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-main-lessons",
            title: t("help:coachMarkers.sidebar.student.main_lessons.title"),
            description: t("help:coachMarkers.sidebar.student.main_lessons.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 3,
          title: t("conversations", "Conversations"),
          icon: <MessageCircle height="1.125rem" width="1.125rem" />,
          path: `${STUDENT_WORKSPACE_PATHS("main").CHATS}`,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").CHATS}/.*$`),
          coachMark: {
            id: "sidebar-student-main-conversations",
            title: t("help:coachMarkers.sidebar.student.main_conversations.title"),
            description: t("help:coachMarkers.sidebar.student.main_conversations.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 4,
          title: t("homeworks", "Homeworks"),
          icon: <Icon height="1.125rem" icon="mdi:clipboard-text" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS("main").HOMEWORKS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").HOMEWORKS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-student-main-homeworks",
            title: t("help:coachMarkers.sidebar.student.main_homeworks.title"),
            description: t("help:coachMarkers.sidebar.student.main_homeworks.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 5,
          title: t("materials", "Materials"),
          icon: <FileText height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS("main").MATERIALS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").MATERIALS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-student-main-materials",
            title: t("help:coachMarkers.sidebar.student.main_materials.title"),
            description: t("help:coachMarkers.sidebar.student.main_materials.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 6,
          title: t("announcements", "Announcements"),
          icon: <Bell height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS("main").ANNOUNCEMENTS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS("main").ANNOUNCEMENTS}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-main-announcements",
            title: t("help:coachMarkers.sidebar.student.main_announcements.title"),
            description: t("help:coachMarkers.sidebar.student.main_announcements.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
      ],
    };

    // Other workspace links for tutor
    const tutorOtherWorkspaceLinks: AppLinkGroup = {
      id: 1,
      label: currentWorkspace?.name || t("workspace"),
      links: [
        {
          id: 1,
          title: t("tutor.overview", "Overview"),
          icon: <HomeIcon height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).HOME,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).HOME}$`),
          coachMark: {
            id: "sidebar-tutor-workspace-overview",
            title: t("help:coachMarkers.sidebar.tutor.workspace_overview.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_overview.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 2,
          title: t("lessons", "Lessons"),
          icon: <BookOpen height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).LESSONS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).LESSONS}(/.*)?$`),
          coachMark: {
            id: "sidebar-tutor-workspace-lessons",
            title: t("help:coachMarkers.sidebar.tutor.workspace_lessons.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_lessons.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 3,
          title: t("conversation", "Conversation"),
          icon: <MessageCircle height="1.125rem" width="1.125rem" />,
          path: `${TUTOR_WORKSPACE_PATHS(workspaceId).CHAT}/${currentWorkspace?.main_conversation_id || ""}`,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).CHAT}/.*$`),
          coachMark: {
            id: "sidebar-tutor-workspace-conversation",
            title: t("help:coachMarkers.sidebar.tutor.workspace_conversation.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_conversation.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 4,
          title: t("materials", "Materials"),
          icon: <FileText height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).MATERIALS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).MATERIALS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-tutor-workspace-materials",
            title: t("help:coachMarkers.sidebar.tutor.workspace_materials.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_materials.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 5,
          title: t("homeworks", "Homeworks"),
          icon: <Icon height="1.125rem" icon="mdi:clipboard-text" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).HOMEWORKS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).HOMEWORKS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-tutor-workspace-homeworks",
            title: t("help:coachMarkers.sidebar.tutor.workspace_homeworks.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_homeworks.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 6,
          title: t("notes", "Notes"),
          icon: <StickyNote height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).NOTES,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).NOTES}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-tutor-workspace-notes",
            title: t("help:coachMarkers.sidebar.tutor.workspace_notes.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_notes.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
        {
          id: 7,
          title: t("settings", "Settings"),
          icon: <Settings height="1.125rem" width="1.125rem" />,
          path: TUTOR_WORKSPACE_PATHS(workspaceId).SETTINGS,
          pattern: new RegExp(`^${TUTOR_WORKSPACE_PATHS(workspaceId).SETTINGS}(/.*)?$`),
          coachMark: {
            id: "sidebar-tutor-workspace-settings",
            title: t("help:coachMarkers.sidebar.tutor.workspace_settings.title"),
            description: t("help:coachMarkers.sidebar.tutor.workspace_settings.description"),
            placement: "right",
            shouldShow: shouldShowCoachMarkers,
          },
        },
      ],
    };

    // Other workspace links for student
    const studentOtherWorkspaceLinks: AppLinkGroup = {
      id: 1,
      label: currentWorkspace?.name || t("workspace"),
      links: [
        {
          id: 1,
          title: t("student.overview", "Overview"),
          icon: <HomeIcon height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).HOME,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).HOME}$`),
          coachMark: {
            id: "sidebar-student-workspace-overview",
            title: t("help:coachMarkers.sidebar.student.workspace_overview.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_overview.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 2,
          title: t("tutor.lessons_schedule"),
          icon: <CalendarIcon height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).SCHEDULE,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).SCHEDULE}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-workspace-schedule",
            title: t("help:coachMarkers.sidebar.student.workspace_schedule.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_schedule.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 3,
          title: t("lessons", "Lessons"),
          icon: <BookOpen height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).LESSONS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).LESSONS}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-workspace-lessons",
            title: t("help:coachMarkers.sidebar.student.workspace_lessons.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_lessons.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 4,
          title: t("conversation", "Conversation"),
          icon: <MessageCircle height="1.125rem" width="1.125rem" />,
          path: `${STUDENT_WORKSPACE_PATHS(workspaceId).CHAT}/${currentWorkspace?.main_conversation_id || ""}`,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).CHAT}/.*$`),
          coachMark: {
            id: "sidebar-student-workspace-conversation",
            title: t("help:coachMarkers.sidebar.student.workspace_conversation.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_conversation.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 5,
          title: t("materials", "Materials"),
          icon: <FileText height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).MATERIALS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).MATERIALS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-student-workspace-materials",
            title: t("help:coachMarkers.sidebar.student.workspace_materials.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_materials.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 6,
          title: t("homeworks", "Homeworks"),
          icon: <Icon height="1.125rem" icon="mdi:clipboard-text" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).HOMEWORKS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).HOMEWORKS}(/.*)?$`),
          soon: true,
          coachMark: {
            id: "sidebar-student-workspace-homeworks",
            title: t("help:coachMarkers.sidebar.student.workspace_homeworks.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_homeworks.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
        {
          id: 7,
          title: t("settings", "Settings"),
          icon: <Settings height="1.125rem" width="1.125rem" />,
          path: STUDENT_WORKSPACE_PATHS(workspaceId).SETTINGS,
          pattern: new RegExp(`^${STUDENT_WORKSPACE_PATHS(workspaceId).SETTINGS}(/.*)?$`),
          coachMark: {
            id: "sidebar-student-workspace-settings",
            title: t("help:coachMarkers.sidebar.student.workspace_settings.title"),
            description: t("help:coachMarkers.sidebar.student.workspace_settings.description"),
            placement: "right",
            shouldShow: shouldShowStudentCoachMarkers,
          },
        },
      ],
    };

    // Helper function to deep clone link groups
    const cloneLinkGroups = (linkGroups: AppLinkGroup[]): AppLinkGroup[] => {
      return linkGroups.map((group) => ({
        ...group,
        links: group.links.map((link) => ({
          ...link,
          children: link.children ? link.children.map((child) => ({ ...child })) : undefined,
        })),
      }));
    };

    // Helper function to modify tutor links based on user state
    const getModifiedTutorLinks = (userState: string): AppLinkGroup[] => {
      const tutorLinks = cloneLinkGroups(baseTutorLinks);

      switch (userState) {
        case "tutorWithoutPayments":
          // Disable all links
          tutorLinks.forEach((group) => {
            group.links.forEach((link) => {
              link.disabled = true;
              if (link.children) {
                link.children.forEach((child) => {
                  child.disabled = true;
                });
              }
            });
          });
          break;
        case "tutorWithoutPricing":
          // Disable everything except pricing in schedule
          tutorLinks.forEach((group) => {
            group.links.forEach((link) => {
              if (link.title === t("tutor.lessons_schedule")) {
                // For lessons_schedule, disable all children except pricing
                if (link.children) {
                  link.children.forEach((child) => {
                    if (child.title !== t("tutor.lessonPricing")) {
                      child.disabled = true;
                    }
                  });
                }
              } else {
                // Disable all other links
                link.disabled = true;
                if (link.children) {
                  link.children.forEach((child) => {
                    child.disabled = true;
                  });
                }
              }
            });
          });
          break;

        case "tutorWithoutProfile":
          // Disable everything except public profile
          tutorLinks.forEach((group) => {
            group.links.forEach((link) => {
              if (link.title !== t("tutor.public_profile")) {
                link.disabled = true;
                if (link.children) {
                  link.children.forEach((child) => {
                    child.disabled = true;
                  });
                }
              }
            });
          });
          break;

        default:
          // No modifications for regular tutor
          break;
      }

      return tutorLinks;
    };

    // Determine which links to show based on user state
    switch (userState) {
      case "tutor":
        return [...baseTutorLinks, tutorMainWorkspaceLinks];

      case "student":
        return [...baseStudentLinks, studentMainWorkspaceLinks];

      case "tutorInWorkspace":
        return [...baseTutorLinks, tutorOtherWorkspaceLinks];

      case "studentInWorkspace":
        return [...baseStudentLinks, studentOtherWorkspaceLinks];

      case "tutorWithoutPayments":
      case "tutorWithoutPricing":
      case "tutorWithoutProfile":
        return getModifiedTutorLinks(userState);

      default:
        return [];
    }
  }, [t, workspaceId, currentWorkspace, userState, workspaceData, user]);

  // Function to check if a link is active based on its pattern
  const isLinkActive = (pattern: RegExp) => {
    return pattern.test(location.pathname);
  };

  return {
    linkGroups,
    isLinkActive,
  };
}

const alwaysCollapsedPagePatterns = [
  new RegExp(`^${PATHS.TUTOR.CALENDAR}(/.*)?`),
  /\/schedule$/,
  new RegExp(".*/lesson/.*/video$"),
  new RegExp(".*/lesson/.*/whiteboard$"),
];

// @ts-ignore
export function isAlwaysCollapsedPage(pathname: string) {
  return alwaysCollapsedPagePatterns.some((pattern) => pattern.test(pathname));
}
